import { type UIEventHandler, useEffect, useRef, useState } from "react";
import { useGetInvoices } from "@/accounts-payable/query";
import type {
  ApDayField,
  ApDayRange,
  ApInvoicesTabFilters,
  ApInvoiceListState,
  ApInvoiceStatus,
  ApReviewState,
} from "@/accounts-payable/types";
import { useDebounce } from "@/core/hooks";

export const INVOICE_FILTERS = [
  { key: "all", label: "All", statuses: [] },
  {
    key: "needs_attention",
    label: "Needs attention",
    statuses: ["match_failed", "parse_failed"],
  },
  { key: "needs_job", label: "Needs a job", statuses: ["match_failed"] },
  { key: "unreadable", label: "Couldn't read", statuses: ["parse_failed"] },
  {
    key: "processing",
    label: "Processing",
    statuses: ["pending_parse", "parsing", "parsed", "matching"],
  },
  { key: "matched", label: "Matched", statuses: ["matched"] },
  {
    key: "needs_review",
    label: "Needs review",
    statuses: [],
    reviewStates: ["needs_review"],
  },
  {
    key: "reviewed",
    label: "Reviewed",
    statuses: [],
    reviewStates: ["reviewed"],
  },
] satisfies {
  key: string;
  label: string;
  statuses: ApInvoiceStatus[];
  reviewStates?: ApReviewState[];
}[];

// Keep state only in this browser page. A refresh starts a new list session.
const lists = new Map<string, ApInvoiceListState>();
const emptyState: ApInvoiceListState = {
  filter: "all",
  search: "",
  dayField: "added",
  dayRange: null,
  scrollTop: 0,
  scrollLeft: 0,
  pageCount: 1,
};

export const useInvoicesTab = (businessId: string | null) => {
  const savedPosition = useRef(
    (businessId && lists.get(businessId)) || emptyState,
  ).current;
  const [filters, setFilters] = useState<ApInvoicesTabFilters>(() => ({
    filter: savedPosition.filter,
    search: savedPosition.search,
    dayField: savedPosition.dayField,
    dayRange: savedPosition.dayRange,
  }));
  const [restored, setRestored] = useState(
    () => !(businessId && lists.has(businessId)),
  );
  const viewportRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(filters.search, 300);
  const activeFilter = INVOICE_FILTERS.find(
    (filter) => filter.key === filters.filter,
  );
  const query = useGetInvoices(businessId, {
    statuses: activeFilter?.statuses,
    reviewStates:
      activeFilter && "reviewStates" in activeFilter
        ? activeFilter.reviewStates
        : undefined,
    search: debouncedSearch,
    added: filters.dayField === "added" ? filters.dayRange : null,
    reviewed: filters.dayField === "reviewed" ? filters.dayRange : null,
  });
  const loadedPageCount = query.data?.pages.length ?? 0;
  const needsMorePages =
    query.hasNextPage && loadedPageCount < savedPosition.pageCount;
  const restoring = !restored;
  const awaitingPosition =
    restoring &&
    !query.isError &&
    (query.isPending ||
      query.isFetching ||
      query.isPlaceholderData ||
      needsMorePages);
  const { fetchNextPage } = query;

  useEffect(() => {
    if (restoring && needsMorePages && !query.isFetching && !query.isError) {
      void fetchNextPage();
    }
  }, [
    restoring,
    needsMorePages,
    loadedPageCount,
    query.isFetching,
    query.isError,
    fetchNextPage,
  ]);

  // SimpleTable's own mount effects run first, including its scroll reset.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!restoring || awaitingPosition || query.isError || !viewport) return;
    if (
      savedPosition.scrollTop >
        viewport.scrollHeight - viewport.clientHeight + 1 &&
      query.hasNextPage
    ) {
      void fetchNextPage();
      return;
    }
    viewport.scrollTop = savedPosition.scrollTop;
    viewport.scrollLeft = savedPosition.scrollLeft;
    setRestored(true);
  }, [
    restoring,
    awaitingPosition,
    query.isError,
    query.hasNextPage,
    savedPosition,
    loadedPageCount,
    fetchNextPage,
  ]);

  const canSaveList =
    !restoring &&
    !query.isPlaceholderData &&
    !query.isPending &&
    debouncedSearch === filters.search;

  // Sync list metadata even when loading another page produces no scroll event.
  useEffect(() => {
    if (!businessId || !canSaveList) return;
    lists.set(businessId, {
      ...(lists.get(businessId) ?? emptyState),
      ...filters,
      pageCount: loadedPageCount,
    });
  }, [businessId, canSaveList, filters, loadedPageCount]);

  const onViewportScroll: UIEventHandler<HTMLDivElement> = (event) => {
    if (!businessId || !canSaveList) return;
    lists.set(businessId, {
      ...filters,
      scrollTop: event.currentTarget.scrollTop,
      scrollLeft: event.currentTarget.scrollLeft,
      pageCount: loadedPageCount,
    });
  };

  const changeFilters = (changes: Partial<ApInvoicesTabFilters>) => {
    const nextFilters = { ...filters, ...changes };
    if (businessId) lists.set(businessId, { ...emptyState, ...nextFilters });
    setFilters(nextFilters);
    setRestored(true);
    if (viewportRef.current) viewportRef.current.scrollTop = 0;
  };

  return {
    query,
    viewportRef,
    onViewportScroll,
    awaitingPosition,
    restoring,
    filter: filters.filter,
    search: filters.search,
    dayField: filters.dayField,
    dayRange: filters.dayRange,
    debouncedSearch,
    setFilter: (filter: string) => changeFilters({ filter }),
    setSearch: (search: string) => changeFilters({ search }),
    setDayField: (dayField: ApDayField) => changeFilters({ dayField }),
    setDayRange: (dayRange: ApDayRange | null) => changeFilters({ dayRange }),
  };
};
